import React, { useState } from 'react';
import { UserPlus, Users, X, ChevronDown } from 'lucide-react';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  job_title: string;
  photo_url: string | null;
}

interface TeamMemberSelectProps {
  teamMembers: TeamMember[];
  selectedTeamMembers: string[];
  onChange: (memberIds: string[]) => void;
}

export default function TeamMemberSelect({ teamMembers, selectedTeamMembers, onChange }: TeamMemberSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectAll = () => {
    onChange(teamMembers.map(m => m.id));
    setIsOpen(false);
  };

  const handleSelectNone = () => {
    onChange([]);
    setIsOpen(false);
  };

  const handleToggleMember = (memberId: string) => {
    if (selectedTeamMembers.includes(memberId)) {
      onChange(selectedTeamMembers.filter(id => id !== memberId));
    } else {
      onChange([...selectedTeamMembers, memberId]);
    }
  };

  const getSelectedMembersText = () => {
    if (selectedTeamMembers.length === 0) return 'Select team members';
    if (selectedTeamMembers.length === teamMembers.length) return 'All team members selected';
    if (selectedTeamMembers.length === 1) {
      const member = teamMembers.find(m => m.id === selectedTeamMembers[0]);
      return member ? member.name : 'One team member selected';
    }
    return `${selectedTeamMembers.length} team members selected`;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white dark:bg-gray-700 text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <div className="flex items-center">
          <Users className="h-5 w-5 text-gray-400 mr-2" />
          <span className="text-sm text-gray-700 dark:text-gray-200">
            {getSelectedMembersText()}
          </span>
        </div>
        <ChevronDown className="h-5 w-5 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleSelectNone}
              className="text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-500"
            >
              Clear
            </button>
          </div>
          <div className="max-h-60 overflow-auto">
            {teamMembers.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => handleToggleMember(member.id)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {member.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {member.job_title}
                  </p>
                </div>
                <div className={`w-4 h-4 rounded border ${
                  selectedTeamMembers.includes(member.id)
                    ? 'border-indigo-500 bg-indigo-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {selectedTeamMembers.includes(member.id) && (
                    <svg className="w-full h-full text-white" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}